import * as THREE from 'three';
import { Random } from '../core/Random';
import {
  bathroomSink,
  bathtub,
  bed,
  beerFridge,
  bigRecliner,
  canPyramid,
  coffeeTable,
  crtTv,
  deerMount,
  dinette,
  dogsPokerPainting,
  doorMat,
  dreamCatcher,
  dresser,
  fishTank,
  flatTv,
  floorLamp,
  gameConsole,
  homeFridge,
  interiorDoor,
  interiorWindow,
  karaokeMachine,
  kitchenCounter,
  lavaLamp,
  neonBeerSign,
  rug,
  singingBass,
  stove,
  tanningBed,
  toilet,
  waterbed,
  wolfTapestry,
} from './builders/furniture';
import { couch } from './builders/props';
import type { CollisionWorld } from './Collision';
import type { Vec2 } from './layout';
import { box, mat, mesh, texMat, worldBox } from './materials';
import {
  ROOM_LAYOUT,
  ROOM_SHAPE,
  roomBounds,
  roomCenter,
  type FurnitureKind,
  type Placement,
  type RoomLayout,
} from './rooms';
import {
  camoTexture,
  floralTexture,
  leopardTexture,
  linoleumTexture,
  panelingTexture,
  racingWallTexture,
  shagTexture,
  woodTexture,
} from './textures';

export type HomeRoomKind = 'single' | 'double';

const STUB = 0.28;
const WALL = 0.12;
const PARTITION_H = 1.15;

/** Footprints (width along local x, depth along local z) for collision. */
const FOOTPRINT: Partial<Record<FurnitureKind | string, [number, number]>> = {
  bed: [1.6, 2.15],
  dresser: [1.1, 0.5],
  couch: [2.0, 0.9],
  couch2: [2.0, 0.9],
  coffeeTable: [1.1, 0.6],
  tv: [1.0, 0.45],
  lamp: [0.3, 0.3],
  stove: [0.76, 0.64],
  fridge: [0.78, 0.7],
  dinette: [1.5, 0.9],
  toilet: [0.48, 0.65],
  tub: [1.6, 0.75],
  bathSink: [0.6, 0.45],
  lazyBoy: [1.0, 1.1],
  waterbed: [1.9, 2.25],
  beerFridge: [0.6, 0.6],
  lavaLamp: [0.5, 0.4],
  fishTank: [1.0, 0.45],
  karaoke: [0.5, 0.4],
  tanningBed: [2.0, 0.95],
  gameConsole: [0.8, 0.45],
  canPyramid: [0.9, 0.4],
};

function wallMaterial(id: string): THREE.MeshStandardMaterial {
  switch (id) {
    case 'floral':
      return texMat(floralTexture());
    case 'camo':
      return texMat(camoTexture());
    case 'leopard':
      return texMat(leopardTexture());
    case 'racing':
      return texMat(racingWallTexture());
    default:
      return texMat(panelingTexture());
  }
}

function floorMaterial(id: string): THREE.MeshStandardMaterial {
  switch (id) {
    case 'avocado':
      return texMat(shagTexture('#7a8a35'), { rough: 1 });
    case 'linoleum':
      return texMat(linoleumTexture(), { rough: 0.4 });
    case 'laminate':
      return texMat(woodTexture(), { color: '#c99a68', rough: 0.55 });
    case 'leopard':
      return texMat(leopardTexture(), { rough: 1 });
    default:
      return texMat(shagTexture('#b5622d'), { rough: 1 });
  }
}

interface Wall {
  upper: THREE.Group;
  /** Outward normal (x, z) and a point on the inside face. */
  nx: number;
  nz: number;
  px: number;
  pz: number;
}

interface PlacedDecor {
  object: THREE.Object3D;
  colliders: number[];
  update?: (time: number, dt: number) => void;
}

/** Shared "view out the window" material: the glow follows the time of day. */
let windowPane: THREE.MeshStandardMaterial | null = null;
export function windowPaneMaterial(): THREE.MeshStandardMaterial {
  windowPane ??= new THREE.MeshStandardMaterial({
    color: '#cfe6f4',
    emissive: new THREE.Color('#cfe6f4'),
    emissiveIntensity: 0.9,
    roughness: 0.2,
  });
  return windowPane;
}

/**
 * The inside of a home: floor, walls in your choice of finish, windows, furniture and
 * whatever you've decorated it with. The walls between the camera and the room cut
 * away so you can see in, dollhouse style.
 */
export class Room {
  readonly group = new THREE.Group();
  readonly center: Vec2;
  readonly layout: RoomLayout;
  private readonly walls: Wall[] = [];
  private readonly colliders: number[] = [];
  private readonly finishMeshes: { walls: THREE.Mesh[]; floor: THREE.Mesh | null } = {
    walls: [],
    floor: null,
  };
  private readonly furniture = new Map<
    FurnitureKind,
    { object: THREE.Object3D; colliders: number[]; p: Placement }
  >();
  private readonly decor = new Map<string, PlacedDecor>();
  private finish = { walls: '', floor: '' };
  /** Only there when the camera's inside (first person, sitting on the couch): the dollhouse view looks down past it. */
  private ceiling: THREE.Group | null = null;
  private tvScreen: THREE.MeshStandardMaterial | null = null;
  /** The TV's glass, for playing a show on it. */
  tvScreenMesh: THREE.Mesh | null = null;
  private readonly rng: Random;
  /** True while somebody's watching TV in here. */
  tvOn = false;

  constructor(
    parent: THREE.Object3D,
    private readonly collision: CollisionWorld,
    readonly slot: number,
    readonly kind: HomeRoomKind,
    finish: { walls: string; floor: string },
  ) {
    this.center = roomCenter(slot);
    this.layout = ROOM_LAYOUT[kind];
    this.rng = new Random(500 + slot);
    this.group.position.set(this.center.x, 0, this.center.z);
    this.buildShell();
    this.setFinish(finish.walls, finish.floor);
    for (const [k, p] of this.layout.furniture) this.placeFurniture(k, p);
    parent.add(this.group);
  }

  get shape(): (typeof ROOM_SHAPE)['single'] {
    return ROOM_SHAPE[this.kind];
  }

  get bounds(): ReturnType<typeof roomBounds> {
    return roomBounds(this.slot, this.kind);
  }

  /** World position of a local point. */
  world(p: Vec2): Vec2 {
    return { x: this.center.x + p.x, z: this.center.z + p.z };
  }

  /** Where you appear when you walk in (just inside the front door). */
  get entry(): Vec2 {
    const s = this.shape;
    return this.world({ x: s.doorX, z: s.depth / 2 - 0.75 });
  }

  /** Stand here to leave. */
  get exit(): Vec2 {
    const s = this.shape;
    return this.world({ x: s.doorX, z: s.depth / 2 - 0.35 });
  }

  /** Where a piece of furniture sits (world), and which way it faces. */
  furnitureAt(kind: FurnitureKind): (Vec2 & { rot: number }) | null {
    const p = this.layout.furniture.find(([k]) => k === kind)?.[1];
    return p ? { ...this.world(p), rot: p.rot ?? 0 } : null;
  }

  /** The object for a piece of furniture or an interior item, for highlighting. */
  objectFor(id: string): THREE.Object3D | null {
    const d = this.decor.get(id);
    if (d) return d.object;
    const f = this.furniture.get(id as FurnitureKind);
    return f && f.object.visible ? f.object : null;
  }

  spot(name: keyof RoomLayout['spots']): Vec2 {
    return this.world(this.layout.spots[name]);
  }

  /** Where an interior item sits, if it fits in this home. */
  decorSpot(id: string): Vec2 | null {
    const d = this.layout.decor[id];
    return d ? this.world(d) : null;
  }

  has(id: string): boolean {
    return this.decor.has(id);
  }

  setFinish(walls: string, floor: string): void {
    if (walls !== this.finish.walls) {
      const m = wallMaterial(walls);
      for (const w of this.finishMeshes.walls) w.material = m;
    }
    if (floor !== this.finish.floor && this.finishMeshes.floor)
      this.finishMeshes.floor.material = floorMaterial(floor);
    this.finish = { walls, floor };
  }

  /** Shows exactly these interior items (the ones that fit). */
  setDecor(items: readonly string[]): void {
    const want = items.filter((id) => id in this.layout.decor);
    for (const id of want) if (!this.decor.has(id)) this.addDecor(id);
    for (const id of [...this.decor.keys()]) if (!want.includes(id)) this.removeDecor(id);
    // Furniture that an item replaces (the waterbed takes the bed's spot).
    for (const [k, f] of this.furniture) {
      const hidden = !!f.p.unless && this.decor.has(f.p.unless);
      if (f.object.visible === hidden) {
        f.object.visible = !hidden;
        for (const id of f.colliders) this.collision.remove(id);
        f.colliders = hidden ? [] : this.collide(k, f.p);
      }
    }
  }

  /** Hide the walls between the camera and the inside of the room. */
  updateCutaway(camera: THREE.Vector3): void {
    for (const w of this.walls) {
      const out = (camera.x - w.px) * w.nx + (camera.z - w.pz) * w.nz;
      w.upper.visible = out < 0.2;
    }
    if (this.ceiling) {
      const b = this.bounds;
      this.ceiling.visible =
        camera.y < this.shape.height &&
        camera.x > b.minX - 0.05 &&
        camera.x < b.maxX + 0.05 &&
        camera.z > b.minZ - 0.05 &&
        camera.z < b.maxZ + 0.05;
    }
  }

  update(time: number, dt: number): void {
    if (this.tvScreen) {
      this.tvScreen.emissiveIntensity = this.tvOn
        ? 0.9 + Math.sin(time * 17) * 0.15 + Math.sin(time * 5.3) * 0.2
        : 0.12;
    }
    for (const d of this.decor.values()) d.update?.(time, dt);
  }

  dispose(): void {
    for (const id of [...this.decor.keys()]) this.removeDecor(id);
    for (const f of this.furniture.values()) for (const id of f.colliders) this.collision.remove(id);
    for (const id of this.colliders) this.collision.remove(id);
    this.group.removeFromParent();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
  }

  // --- building ----------------------------------------------------------------------

  private buildShell(): void {
    const { length: L, depth: D, height: H, doorX } = this.shape;
    const floor = mesh(worldBox(L + 2 * WALL, 0.1, D + 2 * WALL, 1.4), floorMaterial('shag'), 0, -0.05, 0, {
      cast: false,
    });
    this.group.add(floor);
    this.finishMeshes.floor = floor;

    // Four walls: a short stub that always shows, and the rest that cuts away.
    const pane = windowPaneMaterial();
    const curtain = this.rng.pick(['#c75b6b', '#d9b45a', '#6f8f55', '#7a9cc0']);
    const addWall = (len: number, cx: number, cz: number, alongX: boolean, nx: number, nz: number): void => {
      const w = alongX ? len : WALL;
      const d = alongX ? WALL : len;
      const stub = mesh(worldBox(w, STUB, d, 1.2), wallMaterial('paneling'), cx, STUB / 2, cz, {
        cast: false,
      });
      this.group.add(stub);
      this.finishMeshes.walls.push(stub);
      const upper = new THREE.Group();
      const up = mesh(worldBox(w, H - STUB, d, 1.2), wallMaterial('paneling'), cx, STUB + (H - STUB) / 2, cz);
      upper.add(up);
      this.finishMeshes.walls.push(up);
      this.group.add(upper);
      this.walls.push({
        upper,
        nx,
        nz,
        px: this.center.x + cx - nx * (WALL / 2),
        pz: this.center.z + cz - nz * (WALL / 2),
      });
      const b = { minX: cx - w / 2, maxX: cx + w / 2, minZ: cz - d / 2, maxZ: cz + d / 2 };
      this.colliders.push(
        this.collision.addRect(
          this.center.x + (b.minX + b.maxX) / 2,
          this.center.z + (b.minZ + b.maxZ) / 2,
          w,
          d,
        ),
      );
    };
    addWall(L + 2 * WALL, 0, -D / 2 - WALL / 2, true, 0, -1);
    addWall(L + 2 * WALL, 0, D / 2 + WALL / 2, true, 0, 1);
    addWall(D, -L / 2 - WALL / 2, 0, false, -1, 0);
    addWall(D, L / 2 + WALL / 2, 0, false, 1, 0);
    const [back, front] = this.walls;

    // Popcorn ceiling and a dome light or two.
    const ceiling = new THREE.Group();
    const cm = mesh(new THREE.PlaneGeometry(L + 0.2, D + 0.2), mat('#e9e2d2', { rough: 1 }), 0, H, 0, {
      cast: false,
    });
    cm.rotation.x = Math.PI / 2;
    ceiling.add(cm);
    const glow = mat('#fff6dc', { emissive: '#ffe9b8', emissiveIntensity: 1.1 });
    for (const x of this.kind === 'double' ? [-4.5, 1, 5.6] : [-3.6, 2.2]) {
      const dome = mesh(
        new THREE.SphereGeometry(0.22, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2),
        glow,
        x,
        H,
        0,
        {
          cast: false,
        },
      );
      dome.rotation.x = Math.PI;
      ceiling.add(dome);
    }
    ceiling.visible = false;
    this.group.add(ceiling);
    this.ceiling = ceiling;

    // Windows hang on the cutaway part of their wall.
    for (const win of this.layout.windows) {
      const g = interiorWindow(pane, curtain);
      g.position.set(win.x, 0, win.side * (D / 2));
      g.rotation.y = win.side > 0 ? Math.PI : 0;
      (win.side > 0 ? front : back)?.upper.add(g);
    }
    const door = interiorDoor();
    door.position.set(doorX, 0, D / 2);
    door.rotation.y = Math.PI;
    front?.upper.add(door);

    // Half-height interior walls.
    for (const p of this.layout.partitions) {
      const len = Math.hypot(p.b.x - p.a.x, p.b.z - p.a.z);
      const alongX = Math.abs(p.b.x - p.a.x) > Math.abs(p.b.z - p.a.z);
      const cx = (p.a.x + p.b.x) / 2;
      const cz = (p.a.z + p.b.z) / 2;
      const w = alongX ? len : 0.1;
      const d = alongX ? 0.1 : len;
      const part = mesh(worldBox(w, PARTITION_H, d, 1.2), wallMaterial('paneling'), cx, PARTITION_H / 2, cz);
      this.group.add(part);
      this.finishMeshes.walls.push(part);
      // A trim cap so the cut edge reads as a wall, not a sheet of paper.
      this.group.add(
        box(w + 0.02, 0.04, d + 0.02, mat('#e8e0cc'), cx, PARTITION_H + 0.02, cz, { cast: false }),
      );
      this.colliders.push(this.collision.addRect(this.center.x + cx, this.center.z + cz, w, d));
    }
  }

  private placeFurniture(kind: FurnitureKind, p: Placement): void {
    let obj: THREE.Object3D;
    switch (kind) {
      case 'bed':
        obj = bed(this.rng.pick(['#3f5f8a', '#7a2b3a', '#4f6b3a', '#6b4a7a']));
        break;
      case 'dresser':
        obj = dresser();
        break;
      case 'couch':
      case 'couch2':
        obj = couch(kind === 'couch' ? '#8a5a2b' : '#5f7f3a');
        break;
      case 'coffeeTable':
        obj = coffeeTable();
        break;
      case 'tv': {
        const tv = this.kind === 'double' ? flatTv() : crtTv();
        this.tvScreen = tv.screen;
        this.tvScreenMesh = tv.screenMesh;
        obj = tv.group;
        break;
      }
      case 'lamp':
        obj = floorLamp();
        break;
      case 'rug':
        obj = rug(p.w ?? 2, p.d ?? 1.5, this.rng.pick(['#7a3b2b', '#3b4f6b', '#6b5a2b']));
        break;
      case 'sinkCounter':
      case 'counter':
        obj = kitchenCounter(p.w ?? 1.6, kind === 'sinkCounter');
        break;
      case 'stove':
        obj = stove();
        break;
      case 'fridge':
        obj = homeFridge();
        break;
      case 'dinette':
        obj = dinette(this.rng.pick(['#b5402a', '#3f6b8a', '#c79a12']));
        break;
      case 'toilet':
        obj = toilet();
        break;
      case 'tub':
        obj = bathtub();
        break;
      case 'bathSink':
        obj = bathroomSink();
        break;
      case 'mat':
        obj = doorMat();
        break;
    }
    obj.position.set(p.x, obj.position.y, p.z);
    obj.rotation.y = p.rot ?? 0;
    this.group.add(obj);
    this.furniture.set(kind, { object: obj, colliders: this.collide(kind, p), p });
  }

  private collide(kind: string, p: { x: number; z: number; rot?: number; w?: number }): number[] {
    let size = FOOTPRINT[kind];
    if (kind === 'sinkCounter' || kind === 'counter') size = [p.w ?? 1.6, 0.6];
    if (kind === 'tv' && this.kind === 'double') size = [1.8, 0.45];
    if (!size) return [];
    const [w, d] = size;
    const rot = p.rot ?? 0;
    const c = Math.abs(Math.cos(rot));
    const s = Math.abs(Math.sin(rot));
    // Axis-aligned box around the rotated footprint, trimmed a little so you can get close.
    const ww = (w * c + d * s) * 0.92;
    const dd = (w * s + d * c) * 0.92;
    return [this.collision.addRect(this.center.x + p.x, this.center.z + p.z, ww, dd)];
  }

  private addDecor(id: string): void {
    const place = this.layout.decor[id];
    if (!place) return;
    let object: THREE.Object3D;
    let update: PlacedDecor['update'];
    switch (id) {
      case 'dogsPoker':
        object = dogsPokerPainting();
        break;
      case 'wolfTapestry':
        object = wolfTapestry();
        break;
      case 'deerMount':
        object = deerMount();
        break;
      case 'singingBass': {
        const b = singingBass();
        object = b.group;
        let sing = 0;
        this.singBass = () => (sing = 3);
        update = (time, dt) => {
          sing = Math.max(0, sing - dt);
          b.head.rotation.y = sing > 0 ? Math.sin(time * 14) * 0.35 : 0;
        };
        break;
      }
      case 'neonBeer':
        object = neonBeerSign();
        break;
      case 'dreamCatcher':
        object = dreamCatcher();
        break;
      case 'lazyBoy':
        object = bigRecliner();
        break;
      case 'waterbed': {
        const w = waterbed();
        object = w.group;
        update = (time) => {
          w.water.position.y = 0.48 + Math.sin(time * 1.6) * 0.012;
        };
        break;
      }
      case 'beerFridge':
        object = beerFridge();
        break;
      case 'lavaLamp': {
        const l = lavaLamp();
        object = l.group;
        update = (time) => {
          l.blobs.forEach(
            (b, i) => (b.position.y = 0.86 + (Math.sin(time * 0.5 + i * 2.1) * 0.5 + 0.5) * 0.2),
          );
        };
        break;
      }
      case 'fishTank': {
        const f = fishTank();
        object = f.group;
        update = (time) => {
          f.fish.position.x = Math.sin(time * 0.4) * 0.3;
          f.fish.position.y = 0.95 + Math.sin(time * 1.1) * 0.06;
          f.fish.rotation.y = Math.cos(time * 0.4) > 0 ? 0 : Math.PI;
        };
        break;
      }
      case 'karaoke':
        object = karaokeMachine();
        break;
      case 'tanningBed': {
        const t = tanningBed();
        object = t.group;
        update = (time) => (t.glow.emissiveIntensity = 0.5 + Math.sin(time * 3) * 0.15);
        break;
      }
      case 'gameConsole':
        object = gameConsole();
        break;
      case 'canPyramid':
        object = canPyramid(this.rng);
        break;
      default:
        return;
    }
    object.position.set(place.x, place.y ?? 0, place.z);
    object.rotation.y = place.rot;
    this.group.add(object);
    const colliders = place.y === undefined ? this.collide(id, place) : [];
    this.decor.set(id, { object, colliders, update });
  }

  /** Set when the room has a singing bass: makes it sing. */
  singBass: (() => void) | null = null;

  private removeDecor(id: string): void {
    const d = this.decor.get(id);
    if (!d) return;
    if (id === 'singingBass') this.singBass = null;
    d.object.removeFromParent();
    d.object.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    for (const c of d.colliders) this.collision.remove(c);
    this.decor.delete(id);
  }
}
